import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Warehouse } from 'lucide-react';
import backupInfraService from 'features/estoque/services/backupInfraService';
import ProdutosAlmoxarifadosFiltradosTable from './Components/ProdutosAlmoxarifadosFiltradosTable';

/**
 * Estoque de backup de infraestrutura — seis almoxarifados, um por cidade.
 *
 * Não tem período nem alternância de tipo de estoque: saldo é retrato do agora, e o depósito é
 * homogêneo. É a sub-aba mais simples da diretoria, e é assim de propósito.
 *
 * O estado vazio tem texto PRÓPRIO porque, quando esta tela foi escrita, os seis almoxarifados
 * existiam e ainda não tinham recebido material (medido em ago/2026: zero saldo, zero movimento,
 * zero transferência). "Nenhum resultado" leria como defeito; o que está acontecendo é outra coisa.
 *
 * Ferramentas e EPIs reusa a tela inteira trocando serviço e textos: mesma linha, mesma tabela.
 */
const BackupInfra = ({
  service = backupInfraService,
  icone: Icone = Warehouse,
  titulo = 'Estoque de Backup — Infraestrutura',
  descricao = 'Saldo atual dos almoxarifados de backup, por produto e cidade.',
  vazioTitulo = 'Nenhum material nos almoxarifados de backup',
  tabela = 'Saldo por almoxarifado',
  vazioTexto = 'Os seis almoxarifados de backup estão cadastrados e ativos no IXC, mas ainda não receberam nenhuma transferência. Assim que o primeiro material entrar, ele aparece aqui.',
}) => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProdutos(await service.buscarSaldos());
    } catch (erro) {
      console.error(`Erro ao carregar ${titulo}:`, erro);
      setError(service.mensagemDeErro(erro));
      setProdutos([]);
    } finally {
      setLoading(false);
    }
  }, [service, titulo]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    const handleCompanyChange = () => {
      setError(null);
      carregarDados();
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, [carregarDados]);

  const vazio = !loading && !error && produtos.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Icone className="h-5 w-5 text-[color:var(--accent)]" />
          <div>
            <h3 className="text-base font-semibold text-[color:var(--text-primary)]">
              {titulo}
            </h3>
            <p className="text-sm text-[color:var(--text-muted)]">
              {descricao}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={carregarDados}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Recarregando...' : 'Recarregar Dados'}
        </button>
      </div>

      {error && (
        <div className="bg-[color:var(--surface-muted)] border border-[color:var(--border)] rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-[color:var(--status-bad)]" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-[color:var(--text-primary)]">
                Erro ao carregar dados
              </h3>
              <div className="mt-2 text-sm text-[color:var(--text-muted)]">
                <p>{error}</p>
                <p className="mt-1">
                  <button
                    type="button"
                    onClick={carregarDados}
                    className="font-medium underline text-[color:var(--status-bad)] hover:opacity-80"
                  >
                    Tentar novamente
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {vazio ? (
        <div className="bg-[color:var(--surface)] rounded-xl border border-[color:var(--border)] p-8 text-center">
          <Icone className="mx-auto h-8 w-8 text-[color:var(--text-muted)]" />
          <h4 className="mt-3 text-base font-semibold text-[color:var(--text-primary)]">
            {vazioTitulo}
          </h4>
          <p className="mx-auto mt-2 max-w-xl text-sm text-[color:var(--text-muted)]">
            {vazioTexto}
          </p>
        </div>
      ) : (
        <ProdutosAlmoxarifadosFiltradosTable data={produtos} loading={loading} titulo={tabela} />
      )}
    </div>
  );
};

export default BackupInfra;
